/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AiSuggestionWhyDrawer.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Drawer,
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import helper from "../../../../utils/helper";
import CustomToolTip from "../../../../components/infoButton/CustomToolTip";
import { appColor, componentStyle } from "../../../../config/appTheme";


export default function AISuggestionWhyDrawer({
  open,
  onClose,
  recommendation,
}) {
  const tracePaths = recommendation?.explanation?.tracePaths || [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2, //  ABOVE dialog
      }}
    >
      <Box sx={{ width: 520, p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center", // vertical center alignment
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="h6">Why this recommendation?</Typography>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              alignSelf: "center", // explicit vertical alignment
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={2}>
          This shows how the system arrived at this recommendation.
        </Typography>

        {tracePaths.map((path, pIdx) => (
          <Box key={pIdx} mb={3}>
            <Typography fontWeight={600}>Reasoning Path {pIdx + 1}</Typography>

            <Divider
              sx={{
                my: 1,
                borderColor: "#000000ff", // color
                borderBottomWidth: 1, // thickness
              }}
            />

            {[...path]
              .sort((a, b) => {
                const order = [3, 1, 2];
                return (
                  order.indexOf(a.pipelineStep) - order.indexOf(b.pipelineStep)
                );
              })
              .map((step, sIdx) => (
                <Box key={sIdx} sx={{ pl: 1, mb: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={`${sIdx + 1}`}
                      sx={{
                        mb: 0.5,
                        mr: 0.5,
                        backgroundColor: "#232323",
                        color: "white",
                        borderRadius: 0,
                        "& .MuiChip-deleteIcon": {
                          color: "white !important",
                        },
                        "& .MuiChip-deleteIcon:hover": {
                          color: "#f5f5f5 !important",
                        },
                      }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {helper.toSentenceCase(step.type.replaceAll("_", " "))}
                    </Typography>
                  </Stack>

                  <Typography variant="body2">
                    <b>From:</b> {step.from.displayName || step.from.entityId}
                  </Typography>

                  <Typography variant="body2">
                    <b>To:</b> {step.to.displayName || step.to.entityId}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {step.reason}
                  </Typography>

                  {step.metrics && (
                    <Box sx={{ mt: 0.5 }}>
                      {Object.entries(step.metrics).map(([k, v]) => (
                        <CustomToolTip
                          type="string"
                          key={k}
                          title={`${k}: ${v}`}
                          length={k !== "keywords" || String(v).length <= 40}
                          arrow
                          style={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            // fontWeight: "bold",
                          }}
                        >
                          <Chip
                            size="small"
                            label={`${k}: ${v}`}
                            sx={{
                              mb: 0.5,
                              mr: 0.5,
                              backgroundColor: "#232323",
                              color: "white",
                              borderRadius: 0,
                              maxWidth: "100%",
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                              "& .MuiChip-deleteIcon": {
                                color: "white !important",
                              },
                              "& .MuiChip-deleteIcon:hover": {
                                color: "#f5f5f5 !important",
                              },
                            }}
                          />
                        </CustomToolTip>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}

const styles = {
  chipsContainer: {
    maxHeight: "95px",
    maxWidth: "270px",
    overflow: "auto",
    px: 0.5,
    pt: 0.5,
    mb: 3,
  },
  chip: {
    mb: 0.5,
  },
};
